// English UI strings. Must match the shape of fi.ts (type-checked).
import { areasEn } from './area-registry';
import type { CoreMessages, Messages } from './fi';

const enCore: CoreMessages = {
  appName: 'Valyrio udrir',
  nav: {
    label: 'Views',
    today: 'Today',
    free: 'Free practice',
    lessons: 'Lessons',
    grammar: 'Grammar',
    dictionary: 'Dictionary',
    translator: 'Translator',
    letters: 'Letters',
    review: 'Review queue',
    ownWords: 'Own words',
    sentenceStack: 'Sentence stack',
    // Meidän nimet (routes/Names.nav.ts)
    names: 'Our names',
    // Lukupino (routes/Reading.nav.ts)
    reading: 'Reading pile',
    pronunciation: 'Pronunciation',
    listen: 'Show lines',
    shared: 'Shared session',
    settings: 'Settings',
    more: 'More',
    groups: {
      practice: 'Practise',
      learn: 'Learn',
      find: 'Look up and translate',
      together: 'Together',
      app: 'App',
    },
  },
  sync: {
    offline: 'Offline',
    offlineShort: (n) => (n ? `Offline · ${n} waiting` : 'Offline'),
    pendingShort: (n) => `${n} waiting`,
    pending: (n) => `${n} waiting to send`,
    syncing: 'Syncing…',
    saved: 'Saved',
    auth: 'Sign in again',
    error: 'Sync failed',
    retry: 'Try again',
  },
  theme: {
    label: 'Theme',
    system: 'Auto',
    light: 'Light',
    dark: 'Dark',
  },
  lang: { label: 'Interface language', fi: 'Suomi', en: 'English' },
  frontier: {
    lessonOpen: (n) => `Lesson ${n} open for both of you`,
    notStarted: (date) => `The course starts ${date}`,
    next: (date) => `next ${date}`,
    partnerToday: (name, n) => `${name} ${n} today`,
    partnerNotYet: (name) => `${name}: not yet today`,
    youToday: (n) => `You ${n} today`,
    ground: 'free practice',
    paused: 'New words wait until the overdue reviews are done.',
    demo: 'demo',
    partner: 'Partner',
  },
  progress: {
    label: 'Session progress',
    count: (i, n) => `${i} / ${n}`,
  },
  memory: { new: 'new', settling: 'settling', steady: 'steady' },
  card: {
    label: (i, n) => `Card ${i} of ${n}`,
    showAnswer: 'Show answer',
    spaceKey: 'Space',
    check: 'Check',
    dontKnow: "I don't know",
    typeHv: 'Type it in High Valyrian',
    typeHint: 'Alt + a e i o u y types a long vowel, Shift for capitals.',
    typeFirst: 'Type an answer first.',
    ok: 'Correct.',
    near: 'Almost — only long vowels or diacritics differ.',
    wrong: 'Not quite. The right form is below.',
    skip: 'Here is the right form.',
    youTyped: 'You typed',
    alsoRight: (form) => `${form} is also right.`,
    assisted: 'With a keyboard suggestion.',
    toHv: 'in High Valyrian',
    kind: {
      'hv-native': 'word',
      'native-hv': 'typing from English into High Valyrian',
      'form-drill': 'form drill',
      sentence: 'sentence from the translator',
      listen: 'listening card',
    },
    typingFrom: (src) => `typing from ${src === 'fi' ? 'Finnish' : 'English'} into High Valyrian`,
    newWord: 'new word',
    noMeaning: 'No meaning yet.',
    sentenceOfficial: 'official translation',
    sentenceSure: 'a sure translation by the translator',
    sentenceRecall: 'What does the sentence mean?',
  },
  grades: {
    label: 'Grade your recall',
    names: { 1: 'Again', 2: 'Hard', 3: 'Good', 4: 'Easy' },
    returns: (iv) => `in ${iv}`,
  },
  reveal: {
    interlinear: 'Morpheme gloss',
    example: 'Example',
    wikiExample: 'wiki example',
    noSeam: 'This word’s inflection has not been split with confidence yet, so no seam is shown.',
  },
  ours: 'ours',
  lineNote: 'Wiki footnote:',
  pos: {
    noun: 'noun',
    'proper-noun': 'proper noun',
    pronoun: 'pronoun',
    adjective: 'adjective',
    verb: 'verb',
    adverb: 'adverb',
    numeral: 'numeral',
    conjunction: 'conjunction',
    postposition: 'postposition',
    preposition: 'preposition',
    interjection: 'interjection',
    particle: 'particle',
    determiner: 'determiner',
    phrase: 'phrase',
    affix: 'affix',
    other: 'other',
  },
  gender: { lunar: 'lunar', solar: 'solar', terrestrial: 'terrestrial', aquatic: 'aquatic' },
  declension: (d) => {
    if (!/^\d+$/.test(d)) return d === 'irregular' ? 'irregular' : d;
    const n = Number(d);
    const suf = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    return `${n}${suf} declension`;
  },
  cases: {
    nom: 'nominative',
    acc: 'accusative',
    gen: 'genitive',
    dat: 'dative',
    loc: 'locative',
    ins: 'instrumental',
    com: 'comitative',
    voc: 'vocative',
  },
  numbers: { sg: 'singular', pl: 'plural', pc: 'paucal', col: 'collective', pcpl: 'paucal plural', colpl: 'collective plural' },
  flag: {
    button: 'Flag for review',
    title: 'Flag for review',
    kind: 'What is it about?',
    kinds: {
      'fi-meaning': 'Finnish meaning',
      'en-meaning': 'English meaning',
      translator: 'Translator result',
      'missing-word': 'Missing word',
      grammar: 'Grammar',
      lesson: 'Lesson',
      segmentation: 'Seam (morpheme split)',
      form: 'Inflected form',
      example: 'Example',
      bug: 'App bug',
      idea: 'Idea',
      'official-change': 'Official data changed',
      'ours-superseded': 'Peterson has a word for the same meaning',
      'ours-collision': 'Spelled like an own word',
      'need-answered': 'Peterson has the word you needed',
    },
    note: 'Note',
    notePlaceholder: 'e.g. should this be translated as …',
    suggested: 'Suggestion (optional)',
    suggestedPlaceholder: 'e.g. a better translation or form',
    save: 'Save flag',
    cancel: 'Cancel',
    noteRequired: 'Write a short note before saving.',
    flagged: 'Flagged for review · open',
    queued: 'Sends when you are back online.',
    another: 'Add another note',
    flaggedHandled: 'Flagged for review · handled in session',
    flaggedBy: (name) => `flagged by ${name}`,
  },
  macron: { group: 'Long vowels', shift: 'Capital letter' },
  sheet: { close: 'Close' },
  end: {
    door: 'Free practice — only unlocked words',
    more: 'Continue: the next weakest',
    ledger: 'Cards in this session',
  },
  today: {
    nothingDue: 'Today’s cards are done.',
    nothingYet: 'No cards yet.',
    notStarted: (date) => `The course starts ${date}. The first lesson opens for both of you that day.`,
    paused: 'New words wait until the overdue reviews are done. Reviews continue as usual.',
    intro: (n) => (n === 0 ? 'No new words today.' : `${n} new ${n === 1 ? 'word' : 'words'} today.`),
    loading: 'Loading the dictionary…',
    fixture: 'Development data: the real dictionary is not here yet.',
  },
  free: {
    title: 'Free practice',
    line: 'Free practice · only unlocked words',
    empty: 'Free practice reviews words you have already met. Start with today’s cards.',
    start: 'Start',
  },
  record: {
    title: 'Shared record',
    days: (n) => (n === 1 ? 'day studied' : 'days studied'),
    together: (n) => `${n} ${n === 1 ? 'day' : 'days'} together`,
    steady: (n) => (n === 1 ? 'steady word' : 'steady words'),
    you: 'You',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance and language',
    cursor: 'Mouse pointer',
    cursorHv: 'Valyrian',
    cursorSystem: 'System',
    answers: 'Answers',
    study: 'Study',
    reminder: 'Reminder',
    reminderExplain: 'One gentle reminder a day — only if you have not studied yet that day.',
    reminderTime: 'Reminder time',
    reminderOff: 'No reminder',
    reminderSave: 'Save time',
    pace: 'Shared pace',
    setBy: (name) => `${name} sets this for both of you.`,
    setByYou: 'You set this for both of you in the Admin section.',
    theAdmin: 'the admin',
    lastChange: (date, name) => `Last changed ${date} (${name}).`,
    saved: 'Saved. The change applies to both of you.',
    savePace: 'Save pace',
    shared: 'Shared settings',
    sharedNote: 'These apply to both of you and take effect at once.',
    ex: {
      theme: 'Auto follows your device’s own setting.',
      cursor:
        'Valyrian: a basalt arrow with a blue weld mark across its shaft, and a matching hand over links and buttons. System: your computer’s own pointer. The choice applies to this device only.',
      macronNote: 'Gentle: {wrong} (right: {right}) counts as correct, and you see the note “Right – mind the macrons”.',
      macronStrict: 'Strict: {wrong} is “almost right” and earns ½ point.',
      reminderAt: (time) => `The reminder comes at about ${time} for both of you — to each only if they have not studied yet that day.`,
      reminderNone: 'No shared reminder time is set: an evening reminder comes only where its own time is set under Your own reminders.',
      paceDaily: (first, days, after) =>
        `For the first ${days} days ${first} new ${first === 1 ? 'word' : 'words'} a day (about ${first * 7} a week), then ${after} a day (about ${after * 7} a week).`,
      paceLessons: (n) =>
        n === 1
          ? 'A new lesson opens every Saturday once you have both passed the one before.'
          : `${n} lessons open each Saturday once you have both passed the ones before.`,
      pacePause: (n) => `If either of you has more than ${n} overdue cards, new words wait until those are reviewed.`,
    },
    startDate: 'Start date (a Saturday)',
    newFirst: 'New words a day at first',
    firstMonthDays: 'Length of the first period in days',
    newAfter: 'New words a day after that',
    lessonsPerWeek: 'Lessons a week',
    pauseAbove: 'Pause new words when overdue reviews exceed',
    needsOnline: 'Changing the shared settings needs a connection.',
    notSaturday: 'The start date must be a Saturday.',
    account: 'Account',
    signedInAs: (name, username) => `Signed in: ${name}${username ? ` (username ${username})` : ''}`,
    signOut: 'Sign out',
    signOutChecking: 'Sending waiting changes…',
    signOutPending: (n) =>
      n === 1
        ? '1 change has not reached the server yet. If you sign out now, it is lost from this device. Sign out anyway?'
        : `${n} changes have not reached the server yet. If you sign out now, they are lost from this device. Sign out anyway?`,
    signOutAnyway: 'Sign out anyway',
    signOutOffline: 'Signing out needs a connection: without one the session stays open on the server. Try again when you are online.',
    flagApp: 'A note about the app',
    admin: 'Admin',
    technical: 'Technical details',
    pushTest: 'Test notification',
    pushTestNeedsOn: 'Turn on reminders for this device first, then you can send a test.',
    dictionary: 'Dictionary',
    dictLine: (n, date) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'word' : 'words'} · version ${date}`,
    fixture: 'development data',
    unsent: (n) => (n === 1 ? '1 change waiting to send.' : `${n} changes waiting to send.`),
    rejected: (n) =>
      n === 1 ? '1 change was rejected by the server (shown in the review queue).' : `${n} changes were rejected by the server (shown in the review queue).`,
    demo: 'Demo mode: the server could not be reached, so you see an example user. Nothing is sent.',
  },
  update: {
    available: 'A new version is available.',
    reload: 'Update',
    later: 'Later',
    offlineReady: 'The app now works offline too.',
    ok: 'OK',
    // update gate (shell/UpdateGate.tsx)
    checking: 'Checking for updates…',
    updating: 'Updating…',
  },
  more: { title: 'More' },
  inbox: {
    label: 'Waiting for you',
    proposals: (n) => (n === 1 ? 'a word to approve' : `${n} words to approve`),
    picks: (n) => (n === 1 ? 'a candidate to pick' : `${n} needs with candidates to pick`),
    handledFlags: (n) => (n === 1 ? 'a handled flag to close' : `${n} handled flags to close`),
    letters: (n) => (n === 1 ? 'a new letter' : `${n} new letters`),
    corrections: (n) => (n === 1 ? 'a correction to your letter' : `${n} corrections to your letters`),
    count: (n) => `${n} waiting`,
  },
  common: {
    loading: 'Loading…',
    error: 'Could not load the data.',
    retry: 'Try again',
    offlineCopy: (when) => `Offline · showing the copy fetched ${when}.`,
    offlineEmpty: 'Offline. This view needs the server the first time.',
    auth: 'Your sign-in has expired. Reload the page to sign in again.',
    demo: 'Demo mode: the server was not reached, so this view has no data.',
    needsOnline: 'This needs a connection.',
    queued: 'Sends when you are back online.',
    you: 'You',
    partner: 'Partner',
    session: 'Review session',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    send: 'Send',
    delete: 'Delete',
    more: 'Show more',
    indexBuilding: (pct) => `Building the index of inflected forms (${pct}%). Headwords and meanings can already be found.`,
    indexError: 'The index of inflected forms could not be built. Headwords and meanings can still be found.',
  },
  audio: {
    play: 'Listen',
    pause: 'Pause',
    loop: 'Loop',
    loopOn: 'Loop on',
    slow: 'Slow 0.75×',
    peterson: 'Peterson’s recording',
    file: (n, of) => `recording ${n}/${of}`,
    failed: 'The recording could not be played.',
  },
  stress: {
    label: 'Stress',
    hint: 'The stressed syllable is in capitals.',
    none: 'one syllable',
  },
  show: { got: 'GoT', hotd: 'HotD', telltale: 'Telltale', other: '' },
};

export const en: Messages = { ...enCore, ...areasEn };
